import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useBCM } from "@/lib/bcm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Palette, Save, RotateCcw, Sparkles, Image as ImageIcon, Eye,
  Mail, Globe, LayoutDashboard, Menu, FileText, ArrowRight, Upload,
} from "lucide-react";

/* Default Qrew HR brand */
const DEFAULTS = {
  brand: {
    software_name: "Qrew HR",
    tagline: "Smart HR. Stronger Teams.",
    logo_url: "/qrewhr-logo.jpg",
    favicon_url: "/favicon.ico",
    primary_color: "#2563EB",
    secondary_color: "#6D28D9",
    accent_color: "#D946EF",
    gradient_start: "#2563EB",
    gradient_end: "#D946EF",
  },
  login: {
    headline: "Run your global workforce from one smart HR platform.",
    subheadline: "Manage HR, attendance, payroll, performance, recruitment, communication, and AI workflows in one unified platform built for global teams that want to move faster.",
    chip1: "HR & Core", chip2: "Attendance", chip3: "Payroll",
    chip4: "Performance", chip5: "Recruitment", chip6: "AI Workflows",
    show_google: true, show_microsoft: true, show_trial: true, show_workspace: true, show_lang: true,
    footer: "Enterprise-grade security. Your data is always protected.",
  },
  dashboard_widgets: {
    ai_ribbon: true, kpi_cards: true, today_panel: true, qrew_inbox: true,
    attendance_today: true, leave_requests: true, payroll_status: true,
    recruitment_pipeline: true, performance_snapshot: true, workforce_location: true,
    announcements: true, team_activity: true, todays_schedule: true, organization_preview: true,
  },
  sidebar_groups: {
    overview: true, workforce: true, talent: true, payroll_comps: true,
    work: true, analytics: true, admin: true,
  },
  website: {
    hero_headline: "Smart HR. Stronger Teams.",
    hero_subheadline: "The AI-powered HR platform built for global, modern companies.",
    primary_cta: "Start Free Trial", secondary_cta: "Book Demo",
    features_title: "Everything you need to run people operations",
    pricing_title: "Simple pricing for every team size",
    contact_email: "hello@qrewhr.com", support_phone: "+1-555-QREW-HR",
    footer: "© 2026 Qrew HR. All rights reserved.",
  },
  email_pdf: {
    report_header_logo: "/qrewhr-logo.jpg",
    report_footer: "Confidential — Qrew HR · Generated for internal use only.",
    email_footer: "Sent from Qrew HR · Smart HR. Stronger Teams.",
    support_email: "support@qrewhr.com",
    company_address: "Qrew HR Pvt. Ltd., Bangalore, India",
    pdf_primary: "#2563EB",
    pdf_secondary: "#6D28D9",
  },
};

const TABS = [
  { k: "brand",     label: "Brand",          icon: Palette        },
  { k: "login",     label: "Login Page",     icon: Sparkles       },
  { k: "dashboard", label: "Dashboard",      icon: LayoutDashboard},
  { k: "sidebar",   label: "Sidebar/Menu",   icon: Menu           },
  { k: "website",   label: "Website",        icon: Globe          },
  { k: "email_pdf", label: "Email & PDF",    icon: Mail           },
  { k: "preview",   label: "Preview",        icon: Eye            },
];

function ColorInput({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-700">{label}</Label>
      <div className="flex items-center gap-2 mt-1.5">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-12 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5" data-testid={testid} />
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="font-mono text-sm h-10 flex-1" />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid, multiline = false }) {
  const Component = multiline ? Textarea : Input;
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-700">{label}</Label>
      <Component value={value || ""} onChange={(e) => onChange(e.target.value)} type={type} placeholder={placeholder} className="mt-1.5" rows={multiline ? 3 : undefined} data-testid={testid} />
    </div>
  );
}

function ToggleRow({ label, sub, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
      </div>
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

export default function BrandContentManager() {
  const [data, setData] = useState(DEFAULTS);
  const [tab, setTab] = useState("brand");
  const [saving, setSaving] = useState(false);
  const { reload: reloadBCM } = useBCM();

  // Load saved state from backend (BCM API) — falls back to /api/settings for core fields.
  useEffect(() => {
    (async () => {
      try {
        const { data: bcm } = await api.get("/brand-content");
        if (bcm && Object.keys(bcm).length > 0) {
          setData((prev) => ({
            ...prev,
            brand:              { ...prev.brand,              ...(bcm.brand || {}) },
            login:              { ...prev.login,              ...(bcm.login || {}) },
            dashboard_widgets:  { ...prev.dashboard_widgets,  ...(bcm.dashboard_widgets || {}) },
            sidebar_groups:     { ...prev.sidebar_groups,     ...(bcm.sidebar_groups || {}) },
            website:            { ...prev.website,            ...(bcm.website || {}) },
            email_pdf:          { ...prev.email_pdf,          ...(bcm.email_pdf || {}) },
          }));
        }
      } catch {}
      // also pull core settings fallback for first-time tenants
      try {
        const { data: s } = await api.get("/settings");
        setData((prev) => ({
          ...prev,
          brand: {
            ...prev.brand,
            software_name:    prev.brand.software_name || s.software_name || prev.brand.software_name,
            tagline:          prev.brand.tagline       || s.tagline       || prev.brand.tagline,
            logo_url:         prev.brand.logo_url      || s.logo_url      || prev.brand.logo_url,
          },
        }));
      } catch {}
    })();
  }, []);

  const update = (section, k, v) => setData((d) => ({ ...d, [section]: { ...d[section], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      // Persist full BCM state to dedicated endpoint (per-tenant scope).
      await api.put("/brand-content", data);
      // Mirror core fields to /api/settings so existing surfaces (Login, Sidebar) pick them up.
      await api.patch("/settings", {
        software_name:    data.brand.software_name,
        tagline:          data.brand.tagline,
        logo_url:         data.brand.logo_url,
        favicon_url:      data.brand.favicon_url,
        primary_color:    data.brand.primary_color,
        secondary_color:  data.brand.secondary_color,
        accent_color:     data.brand.accent_color,
        login_headline:           data.login.headline,
        login_welcome_subtitle:   data.login.subheadline,
      }).catch(() => {});
      // Apply primary/accent CSS vars live + reload context provider so dashboard reflects toggles.
      document.documentElement.style.setProperty("--qrew-primary", data.brand.primary_color);
      document.documentElement.style.setProperty("--qrew-accent",  data.brand.accent_color);
      await reloadBCM();
      toast.success("Brand & Content saved — settings now active across the workspace.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = () => {
    if (!window.confirm("Reset all branding to Qrew HR defaults?")) return;
    setData(DEFAULTS);
    toast.success("Reset to defaults (click Save to persist)");
  };

  const grad = `linear-gradient(90deg, ${data.brand.gradient_start}, ${data.brand.secondary_color}, ${data.brand.gradient_end})`;

  return (
    <div className="space-y-5" data-testid="brand-content-manager">
      {/* Header */}
      <Card className="p-6 border-slate-200 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-md">
            <Palette className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-violet-100 text-violet-800 border-violet-200 text-[10px] uppercase tracking-wider font-bold">Beta</Badge>
              <span className="text-[10px] uppercase tracking-[0.2em] text-violet-700 font-bold">Platform Console</span>
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 font-[Outfit]">Brand & Content Manager</h1>
            <p className="text-sm text-slate-600 mt-1">Edit logo, colors, login text, dashboard widgets, sidebar visibility — without touching code.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" onClick={reset} data-testid="bcm-reset"><RotateCcw className="w-3.5 h-3.5 mr-1.5"/> Reset</Button>
            <Button onClick={save} disabled={saving} className="text-white font-semibold" style={{ background: grad }} data-testid="bcm-save">
              <Save className="w-3.5 h-3.5 mr-1.5"/> {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab} className="space-y-5">
        <TabsList className="bg-white border border-slate-200 p-1 h-auto flex flex-wrap">
          {TABS.map((t) => (
            <TabsTrigger key={t.k} value={t.k} className="data-[state=active]:bg-violet-100 data-[state=active]:text-violet-800 px-3 h-9 text-xs font-semibold gap-1.5" data-testid={`bcm-tab-${t.k}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ===== BRAND ===== */}
        <TabsContent value="brand">
          <div className="grid lg:grid-cols-3 gap-5">
            <Card className="p-6 border-slate-200 shadow-sm lg:col-span-2 space-y-4">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Identity</div>
              <Field label="Software Name" value={data.brand.software_name} onChange={(v) => update("brand", "software_name", v)} placeholder="Qrew HR" testid="bcm-software-name" />
              <Field label="Tagline" value={data.brand.tagline} onChange={(v) => update("brand", "tagline", v)} placeholder="Smart HR. Stronger Teams." testid="bcm-tagline" />
              <Field label="Logo URL" value={data.brand.logo_url} onChange={(v) => update("brand", "logo_url", v)} placeholder="/qrewhr-logo.jpg" testid="bcm-logo-url" />
              <Field label="Favicon URL" value={data.brand.favicon_url} onChange={(v) => update("brand", "favicon_url", v)} testid="bcm-favicon-url" />
              <div className="text-[10px] text-slate-400">Logo upload (S3 storage) coming soon — paste a public URL for now.</div>

              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mt-6 mb-2">Colors</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <ColorInput label="Primary"   value={data.brand.primary_color}   onChange={(v) => update("brand", "primary_color", v)}   testid="bcm-color-primary" />
                <ColorInput label="Secondary" value={data.brand.secondary_color} onChange={(v) => update("brand", "secondary_color", v)} testid="bcm-color-secondary" />
                <ColorInput label="Accent"    value={data.brand.accent_color}    onChange={(v) => update("brand", "accent_color", v)}    testid="bcm-color-accent" />
                <ColorInput label="Gradient End" value={data.brand.gradient_end} onChange={(v) => update("brand", "gradient_end", v)}    testid="bcm-color-gradient-end" />
              </div>
            </Card>

            {/* Live preview */}
            <Card className="p-6 border-slate-200 shadow-sm space-y-4 self-start sticky top-20" data-testid="bcm-brand-preview">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Live Preview</div>
              <div className="rounded-xl border border-slate-200 p-4 bg-gradient-to-br from-slate-50 to-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-white ring-1 ring-slate-200">
                    <img src={data.brand.logo_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold font-[Outfit] text-slate-900 truncate">{data.brand.software_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{data.brand.tagline}</div>
                  </div>
                </div>
                <Button className="w-full mt-4 text-white font-semibold" style={{ background: grad }}>Primary Action</Button>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <div className="h-8 rounded-md" style={{ background: data.brand.primary_color }} />
                  <div className="h-8 rounded-md" style={{ background: data.brand.secondary_color }} />
                  <div className="h-8 rounded-md" style={{ background: data.brand.accent_color }} />
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* ===== LOGIN ===== */}
        <TabsContent value="login">
          <Card className="p-6 border-slate-200 shadow-sm space-y-4">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Hero copy</div>
            <Field label="Headline" value={data.login.headline} onChange={(v) => update("login", "headline", v)} multiline testid="bcm-login-headline" />
            <Field label="Subheadline" value={data.login.subheadline} onChange={(v) => update("login", "subheadline", v)} multiline testid="bcm-login-subheadline" />

            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 pt-3">Feature chips</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {[1,2,3,4,5,6].map((i) => (
                <Field key={i} label={`Chip ${i}`} value={data.login[`chip${i}`]} onChange={(v) => update("login", `chip${i}`, v)} testid={`bcm-chip-${i}`} />
              ))}
            </div>

            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 pt-3">Visibility</div>
            <div className="grid sm:grid-cols-2 gap-2">
              <ToggleRow label="Google sign-in"      sub="Show 'Sign in with Google'"     value={data.login.show_google}    onChange={(v) => update("login", "show_google", v)}    testid="bcm-toggle-google" />
              <ToggleRow label="Microsoft sign-in"   sub="Show 'Sign in with Microsoft'"  value={data.login.show_microsoft} onChange={(v) => update("login", "show_microsoft", v)} testid="bcm-toggle-microsoft" />
              <ToggleRow label="Free Trial link"     sub="Show 'Start your free trial'"   value={data.login.show_trial}     onChange={(v) => update("login", "show_trial", v)}     testid="bcm-toggle-trial" />
              <ToggleRow label="Find Workspace link" sub="Show 'Find my workspace'"       value={data.login.show_workspace} onChange={(v) => update("login", "show_workspace", v)} testid="bcm-toggle-workspace" />
              <ToggleRow label="Language Selector"   sub="Show top-right region picker"   value={data.login.show_lang}      onChange={(v) => update("login", "show_lang", v)}      testid="bcm-toggle-lang" />
            </div>

            <Field label="Security footer text" value={data.login.footer} onChange={(v) => update("login", "footer", v)} testid="bcm-login-footer" />
          </Card>
        </TabsContent>

        {/* ===== DASHBOARD ===== */}
        <TabsContent value="dashboard">
          <Card className="p-6 border-slate-200 shadow-sm space-y-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Dashboard widget visibility</div>
            <p className="text-sm text-slate-600 mb-2">Toggle which widgets render on the HR Workspace dashboard. Saved per tenant.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                ["ai_ribbon",            "AI Copilot Ribbon",     "Top banner with 3 sample prompts"],
                ["kpi_cards",            "KPI Cards",             "Total Employees, Active Today, On Leave, etc."],
                ["today_panel",          "Today Panel (right rail)", "Avatar, clock, check-in/out, schedule"],
                ["qrew_inbox",           "Qrew Inbox",            "Pending approvals snapshot"],
                ["attendance_today",     "Attendance Today",      "Present/Absent/WFH donut"],
                ["leave_requests",       "Leave Requests",        "Recent leaves with avatars"],
                ["payroll_status",       "Payroll Status",        "Data Locked → Verified → Approved → Processed"],
                ["recruitment_pipeline", "Recruitment Pipeline",  "Funnel chart with stages"],
                ["performance_snapshot", "Performance Snapshot",  "Rating distribution"],
                ["workforce_location",   "Workforce by Location", "Country split widget"],
                ["announcements",        "Announcements",         "Company-wide updates"],
                ["team_activity",        "Team Activity",         "Recent activity feed"],
                ["todays_schedule",      "Today's Schedule",      "Meetings calendar"],
                ["organization_preview", "Organization Preview",  "Top 6 employees with avatars"],
              ].map(([k, label, sub]) => (
                <ToggleRow key={k} label={label} sub={sub} value={data.dashboard_widgets[k]} onChange={(v) => update("dashboard_widgets", k, v)} testid={`bcm-widget-${k}`} />
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* ===== SIDEBAR ===== */}
        <TabsContent value="sidebar">
          <Card className="p-6 border-slate-200 shadow-sm space-y-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Sidebar group visibility</div>
            <p className="text-sm text-slate-600 mb-2">Hide entire menu groups for tenants who don't use them. Individual modules can be controlled via Feature Controls.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                ["overview",      "Overview",             "Dashboard, Inbox, AI Assistant"],
                ["workforce",     "Workforce",            "Employees, Org, Attendance, Leaves, etc."],
                ["talent",        "Talent",               "Recruitment, Onboarding, Learning"],
                ["payroll_comps", "Payroll & Comps",      "Payroll, Expenses & Claims, Assets"],
                ["work",          "Work & Collab",        "Tasks, Projects, Meetings"],
                ["analytics",     "Analytics & Reports",  "Documents, Reports, AI Copilots"],
                ["admin",         "Admin",                "Departments, Holidays, Settings, etc."],
              ].map(([k, label, sub]) => (
                <ToggleRow key={k} label={label} sub={sub} value={data.sidebar_groups[k]} onChange={(v) => update("sidebar_groups", k, v)} testid={`bcm-group-${k}`} />
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* ===== WEBSITE ===== */}
        <TabsContent value="website">
          <Card className="p-6 border-slate-200 shadow-sm space-y-4">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Public website content</div>
            <p className="text-sm text-slate-600">Edit the marketing website (qrewhr.com) without touching code. Changes apply on next site rebuild.</p>

            <div className="grid lg:grid-cols-2 gap-4">
              <Field label="Hero Headline" value={data.website.hero_headline} onChange={(v) => update("website", "hero_headline", v)} testid="bcm-web-hero-headline" />
              <Field label="Hero Subheadline" value={data.website.hero_subheadline} onChange={(v) => update("website", "hero_subheadline", v)} testid="bcm-web-hero-subheadline" />
              <Field label="Primary CTA" value={data.website.primary_cta} onChange={(v) => update("website", "primary_cta", v)} testid="bcm-web-primary-cta" />
              <Field label="Secondary CTA" value={data.website.secondary_cta} onChange={(v) => update("website", "secondary_cta", v)} testid="bcm-web-secondary-cta" />
              <Field label="Features Section Title" value={data.website.features_title} onChange={(v) => update("website", "features_title", v)} testid="bcm-web-features-title" />
              <Field label="Pricing Section Title" value={data.website.pricing_title} onChange={(v) => update("website", "pricing_title", v)} testid="bcm-web-pricing-title" />
              <Field label="Contact Email" value={data.website.contact_email} onChange={(v) => update("website", "contact_email", v)} type="email" testid="bcm-web-contact-email" />
              <Field label="Support Phone" value={data.website.support_phone} onChange={(v) => update("website", "support_phone", v)} testid="bcm-web-support-phone" />
            </div>
            <Field label="Footer text" value={data.website.footer} onChange={(v) => update("website", "footer", v)} testid="bcm-web-footer" />
          </Card>
        </TabsContent>

        {/* ===== EMAIL & PDF ===== */}
        <TabsContent value="email_pdf">
          <Card className="p-6 border-slate-200 shadow-sm space-y-4">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Email & PDF branding</div>
            <div className="grid lg:grid-cols-2 gap-4">
              <Field label="Report Header Logo URL" value={data.email_pdf.report_header_logo} onChange={(v) => update("email_pdf", "report_header_logo", v)} testid="bcm-pdf-logo" />
              <Field label="Support Email"          value={data.email_pdf.support_email}      onChange={(v) => update("email_pdf", "support_email", v)} testid="bcm-pdf-support-email" />
              <Field label="Company Address"        value={data.email_pdf.company_address}    onChange={(v) => update("email_pdf", "company_address", v)} multiline testid="bcm-pdf-address" />
              <Field label="Email Footer"           value={data.email_pdf.email_footer}       onChange={(v) => update("email_pdf", "email_footer", v)} multiline testid="bcm-pdf-email-footer" />
              <Field label="Report Footer"          value={data.email_pdf.report_footer}      onChange={(v) => update("email_pdf", "report_footer", v)} multiline testid="bcm-pdf-report-footer" />
              <ColorInput label="PDF Primary Color"   value={data.email_pdf.pdf_primary}   onChange={(v) => update("email_pdf", "pdf_primary", v)}   testid="bcm-pdf-primary" />
              <ColorInput label="PDF Secondary Color" value={data.email_pdf.pdf_secondary} onChange={(v) => update("email_pdf", "pdf_secondary", v)} testid="bcm-pdf-secondary" />
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Advanced template builder coming soon — for now, edit per-template overrides at code level.
            </div>
          </Card>
        </TabsContent>

        {/* ===== PREVIEW ===== */}
        <TabsContent value="preview">
          <div className="grid lg:grid-cols-2 gap-5">
            {/* Login mock */}
            <Card className="p-6 border-slate-200 shadow-sm" data-testid="bcm-preview-login">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-4">Login preview</div>
              <div className="rounded-2xl bg-gradient-to-br from-slate-50 to-fuchsia-50/50 p-5 border border-slate-200">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-10 h-10 rounded-xl overflow-hidden bg-white ring-1 ring-slate-200"><img src={data.brand.logo_url} alt="" className="w-full h-full object-cover" /></div>
                  <div className="font-bold text-slate-900 font-[Outfit]">{data.brand.software_name}</div>
                </div>
                <div className="text-[20px] font-bold text-slate-900 leading-tight">{data.login.headline}</div>
                <div className="text-xs text-slate-600 mt-2 line-clamp-3">{data.login.subheadline}</div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {[data.login.chip1, data.login.chip2, data.login.chip3, data.login.chip4, data.login.chip5, data.login.chip6].map((c, i) => (
                    <span key={i} className="text-[10px] font-semibold px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-700">{c}</span>
                  ))}
                </div>
                <Button className="w-full mt-4 text-white font-semibold" style={{ background: grad }}>Sign in <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></Button>
              </div>
            </Card>

            {/* Sidebar mock */}
            <Card className="p-6 border-slate-200 shadow-sm" data-testid="bcm-preview-sidebar">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-4">Sidebar preview</div>
              <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-1">
                <div className="flex items-center gap-2.5 p-2 border-b border-slate-100 mb-2">
                  <div className="w-9 h-9 rounded-lg overflow-hidden bg-slate-50 ring-1 ring-slate-200"><img src={data.brand.logo_url} alt="" className="w-full h-full object-cover" /></div>
                  <div className="min-w-0">
                    <div className="font-bold text-sm text-slate-900 truncate">{data.brand.software_name}</div>
                    <div className="text-[9px] text-slate-500 truncate">{data.brand.tagline}</div>
                  </div>
                </div>
                {Object.entries(data.sidebar_groups).filter(([, v]) => v).map(([k]) => (
                  <div key={k} className="text-[10px] uppercase tracking-wider font-bold text-slate-400 px-2 pt-1.5">{k.replace(/_/g, " ")}</div>
                ))}
                <div className="px-2 py-2 rounded-md text-xs font-semibold" style={{ background: `${data.brand.primary_color}15`, color: data.brand.primary_color }}>
                  Dashboard (active)
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
